import styles from './styles.module.scss'
import IcInfoCircle from 'emailThreadViewer/icons/IcInfoCircle'
import IcChevron from 'emailThreadViewer/icons/IcChevron'
import React, { useEffect, useState } from 'react'
import { TextFinderObserver } from 'emailThreadViewer/TextFinder'
import i18n from 'i18n'

interface InfoItem {
  key: string
  value: string
}

interface AiInfosProps {
  infos?: InfoItem[]
}

interface MoreButtonProps {
  isExpanded: boolean
  onClick: () => void
}

interface FoldableProps {
  children: React.ReactNode
  isExpanded: boolean
}

const AiInfos: React.FC<AiInfosProps> = ({ infos }) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false)

  if (!infos?.length) return null

  const firstInfo = infos[0]
  const restInfos = infos.slice(1)

  const hasMore = restInfos.length > 0

  const toggleExpanded = (): void => setIsExpanded((p) => !p)

  return (
    <div className={styles.infos} key="infos">
      <div>
        <div className={styles.infoItem}>
          <div className={styles.symbolContainer}>
            <IcInfoCircle />
          </div>
          <div className={styles.keyValue}>
            {firstInfo.key}
            <span className={styles.value}> {firstInfo.value}</span>
          </div>
        </div>

        {hasMore && (
          <TextFinderObserver
            onContains={(contains: boolean) => contains && setIsExpanded(true)}
            onFocus={({ focused }: { focused: boolean }) => focused && setIsExpanded(true)}
          >
            <Foldable isExpanded={isExpanded}>
              {restInfos.map((info, idx) => (
                <div className={styles.infoItem} key={`info-${idx}-${info.key}`}>
                  <div className={styles.symbolContainer} />
                  <div className={styles.keyValue}>
                    {info.key}
                    <span className={styles.value}> {info.value}</span>
                  </div>
                </div>
              ))}
            </Foldable>
          </TextFinderObserver>
        )}
        {hasMore && <MoreButton isExpanded={isExpanded} onClick={toggleExpanded} />}
      </div>
    </div>
  )
}

const MoreButton: React.FC<MoreButtonProps> = ({ isExpanded, onClick }) => {
  const mounted = useMounted(500)

  return (
    <div className={styles.moreButtonContainer}>
      <button className={styles.moreButton} onClick={onClick}>
        {isExpanded ? i18n.t('email.aiExtraction.hide') : i18n.t('email.aiExtraction.showMore')}
        <IcChevron
          direction={isExpanded ? 'up' : 'down'}
          style={{ transition: mounted ? 'rotate 0.3s ease-in-out' : 'none' }}
        />
      </button>
    </div>
  )
}

const Foldable: React.FC<FoldableProps> = ({ children, isExpanded }) => {
  const mounted = useMounted(500)

  return (
    <div className={styles.foldable} data-expanded={isExpanded} data-animation={mounted}>
      <div>{children}</div>
    </div>
  )
}

const useMounted = (delay: number = 0): boolean => {
  const [mounted, setMounted] = useState<boolean>(false)

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), delay)
    return () => clearTimeout(timer)
  }, [])

  return mounted
}

export default AiInfos
